import React from 'react';
import AwesomeSlider from 'react-awesome-slider';
import 'react-awesome-slider/dist/styles.css';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import MicNoneOutlined from '@mui/icons-material/MicNoneOutlined';
import { apiCapabilities, lifeTaggerApi } from '../api/lifetaggerApi';
import LifeObjectRenderer from './LifeObjectRenderer';
import { lifeTaggerDesign } from '../design/tokens';
import { lifeTaggerTheme } from '../design/LifeTaggerTheme';
import {
  addLocationToContext,
  addResolvedAnchor,
  createInitialContext,
} from './contextState';
import {
  availabilityMessage,
  getExperienceAvailability,
} from './experienceAvailability';
import { getContextOutcome } from './contextOutcome';

const capabilities = ['text', 'html', 'image', 'video', 'audio', 'chatbot'];

export default function ContextualLifeCode({ match }) {
  const identifier = match.params.identifier;
  const token = match.params.token;
  const [state, setState] = React.useState({
    loading: true,
    error: null,
    response: null,
  });
  const [context, setContext] = React.useState(() =>
    createInitialContext(identifier)
  );
  const [recording, setRecording] = React.useState(false);
  const [locationState, setLocationState] = React.useState('idle');
  const [audioState, setAudioState] = React.useState('idle');
  const recorderRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  const audioTimerRef = React.useRef(null);
  const cancelAudioRef = React.useRef(false);

  const resolve = React.useCallback(
    async (nextContext) => {
      setState((current) => ({ ...current, loading: true, error: null }));
      try {
        const resolver = token
          ? lifeTaggerApi.resolvePushExperience
          : lifeTaggerApi.resolveContext;
        const response = await resolver({
          ...(token ? { token } : {}),
          anchors: nextContext.anchors,
          ...(nextContext.location ? { location: nextContext.location } : {}),
          radiusMeters: 100,
          capabilities,
          deviceType: 'web',
        });
        setState({ loading: false, error: null, response });
        if (apiCapabilities.perceptionRecording) {
          await Promise.allSettled(
            response.objects.map((item) =>
              lifeTaggerApi.recordPerception({
                lifeObjectId: item.id,
                anchorIds: response.contextSnapshot?.resolvedAnchorIds || [],
                deviceType: 'web',
                perceivedAt: new Date().toISOString(),
              })
            )
          );
        }
      } catch (error) {
        setState({ loading: false, error: error.message, response: null });
      }
    },
    [token]
  );

  React.useEffect(() => {
    const initialContext = createInitialContext(identifier);
    setContext(initialContext);
    resolve(initialContext);
  }, [identifier, resolve]);

  React.useEffect(
    () => () => {
      clearTimeout(audioTimerRef.current);
      recorderRef.current?.stream?.getTracks().forEach((track) => track.stop());
    },
    []
  );

  const addLocation = () => {
    if (!navigator.geolocation) {
      setLocationState('unavailable');
      setState((current) => ({
        ...current,
        error: 'Location is not available in this browser.',
      }));
      return;
    }
    setLocationState('requesting');
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const nextContext = addLocationToContext(context, coords);
        setContext(nextContext);
        setLocationState('added');
        resolve(nextContext);
      },
      (locationError) => {
        setLocationState(locationError.code === 1 ? 'denied' : 'error');
        setState((current) => ({
          ...current,
          error:
            'Location was not shared. The LifeCode experience still works without it.',
        }));
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const blobToBase64 = (blob) =>
    new Promise((resolveBase64, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolveBase64(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const resolveRecordedAudio = async (blob) => {
    setState((current) => ({ ...current, loading: true, error: null }));
    try {
      const signal = await lifeTaggerApi.resolveSignal({
        type: 'audio',
        data: await blobToBase64(blob),
        metadata: { mimeType: blob.type, durationHintSeconds: 8 },
      });
      if (!signal.resolved) {
        setAudioState('no-match');
        setState((current) => ({
          ...current,
          loading: false,
          error: 'No additional audio context was recognized.',
        }));
        return;
      }
      const nextContext = addResolvedAnchor(context, signal.anchor);
      setContext(nextContext);
      setAudioState('added');
      await resolve(nextContext);
    } catch (error) {
      setState((current) => ({
        ...current,
        loading: false,
        error: error.message || 'Audio context could not be checked.',
      }));
      setAudioState('error');
    }
  };

  const startAudio = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      setAudioState('unavailable');
      setState((current) => ({
        ...current,
        error: 'Audio context is not available in this browser.',
      }));
      return;
    }
    setAudioState('requesting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      cancelAudioRef.current = false;
      recorder.ondataavailable = (event) => {
        if (event.data.size) chunksRef.current.push(event.data);
      };
      recorder.onstop = async () => {
        clearTimeout(audioTimerRef.current);
        stream.getTracks().forEach((track) => track.stop());
        setRecording(false);
        if (cancelAudioRef.current) {
          setAudioState('idle');
          return;
        }
        setAudioState('resolving');
        await resolveRecordedAudio(
          new Blob(chunksRef.current, { type: recorder.mimeType })
        );
      };
      recorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      setAudioState('recording');
      audioTimerRef.current = setTimeout(() => recorder.stop(), 8000);
    } catch (error) {
      setAudioState(error?.name === 'NotAllowedError' ? 'denied' : 'error');
      setState((current) => ({
        ...current,
        error:
          'Microphone access was not shared. This experience still works without it.',
      }));
    }
  };

  const stopAudio = () => recorderRef.current?.stop();
  const cancelAudio = () => {
    cancelAudioRef.current = true;
    recorderRef.current?.stop();
  };

  const experiences = (state.response?.objects || []).map((item) => ({
    item,
    availability: getExperienceAvailability(item),
  }));
  const availableExperiences = experiences.filter(
    ({ availability }) => availability.available
  );
  const unavailableExperiences = experiences.filter(
    ({ availability }) => !availability.available
  );
  const contextOutcome =
    token && state.response && !state.response.objects?.length
      ? {
          message:
            'No LifeObjects from this notification are available in your current context. Sign in if they were shared privately, or check your location.',
        }
      : getContextOutcome(state.response);

  return (
    <ThemeProvider theme={lifeTaggerTheme}>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#fff',
          fontFamily: lifeTaggerDesign.type.body,
        }}
      >
        <Box>
          <Stack>
            {!state.loading && (
              <Stack
                component="nav"
                aria-label="Discovery options"
                direction="row"
                spacing={1}
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 20,
                  px: 2,
                  py: 1.25,
                  minHeight: 64,
                  bgcolor: 'background.paper',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'center',
                  justifyContent: { xs: 'center', sm: 'flex-end' },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    mr: { sm: 'auto' },
                  }}
                >
                  Discover more
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<LocationOnOutlined />}
                  onClick={addLocation}
                  disabled={locationState === 'requesting'}
                >
                  {locationState === 'requesting'
                    ? 'Checking location…'
                    : ['denied', 'error'].includes(locationState)
                    ? 'Try location again'
                    : locationState === 'added'
                    ? 'Update location'
                    : 'Use location'}
                </Button>
                <Button
                  variant={recording ? 'contained' : 'outlined'}
                  size="small"
                  startIcon={<MicNoneOutlined />}
                  color={recording ? 'error' : 'primary'}
                  disabled={['requesting', 'resolving'].includes(audioState)}
                  onClick={recording ? stopAudio : startAudio}
                >
                  {recording
                    ? 'Use this audio'
                    : audioState === 'requesting'
                    ? 'Requesting microphone…'
                    : audioState === 'resolving'
                    ? 'Recognizing audio…'
                    : ['denied', 'error', 'no-match'].includes(audioState)
                    ? 'Try audio again'
                    : 'Listen'}
                </Button>
                {recording && (
                  <Button variant="text" onClick={cancelAudio}>
                    Cancel
                  </Button>
                )}
              </Stack>
            )}
            {state.error && <Alert severity="info">{state.error}</Alert>}
            {state.loading && (
              <Box sx={{ py: 8, textAlign: 'center' }}>
                <CircularProgress />
              </Box>
            )}
            {!state.loading && contextOutcome.message && (
              <Alert severity="info">{contextOutcome.message}</Alert>
            )}
            {availableExperiences.length === 1 && (
              <LifeObjectRenderer
                lifeObject={availableExperiences[0].item}
                unframed
              />
            )}
            {availableExperiences.length > 1 && (
              <AwesomeSlider
                infinite={false}
                key={`${token || identifier}:${availableExperiences
                  .map(({ item }) => item.id)
                  .join(',')}`}
                bullets={availableExperiences.length > 1}
                style={{
                  height: 'calc(100dvh - 64px)',
                  '--slider-height-percentage': '100%',
                  '--content-background-color': 'transparent',
                }}
              >
                {availableExperiences.map(({ item }) => (
                  <div
                    key={item.id}
                    style={{ width: '100%', height: '100%', overflowY: 'auto' }}
                  >
                    <LifeObjectRenderer lifeObject={item} unframed />
                  </div>
                ))}
              </AwesomeSlider>
            )}
            {unavailableExperiences.map(({ item, availability }) => (
              <Alert key={item.id} severity="info">
                <strong>{item.name}</strong> —{' '}
                {availabilityMessage(availability)}
              </Alert>
            ))}
          </Stack>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
